import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input, Button, Alert } from '../components/design-system';
import { Mail, Lock, User, Phone, Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await register(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        formData.phoneNumber
      );
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
            <p className="text-sm text-gray-600 mb-4">
              Please check your email to verify your account.
            </p>
            <p className="text-xs text-gray-500">Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-xl p-6">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 bg-primary-500 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30">
              <Truck className="h-8 w-8 text-white" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-xl font-bold text-gray-900 mb-1">{t('auth.createAccount')}</h1>
            <p className="text-sm text-gray-600">{t('auth.createAccountDescription')}</p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="error" className="mb-4" dismissible onDismiss={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={t('auth.firstName')}
                type="text"
                placeholder="John"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                leftIcon={<User className="h-5 w-5 text-gray-400" />}
                autoComplete="given-name"
                required
                fullWidth
              />
              <Input
                label={t('auth.lastName')}
                type="text"
                placeholder="Doe"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                leftIcon={<User className="h-5 w-5 text-gray-400" />}
                autoComplete="family-name"
                required
                fullWidth
              />
            </div>

            <Input
              label={t('auth.email')}
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange('email')}
              leftIcon={<Mail className="h-5 w-5 text-gray-400" />}
              autoComplete="email"
              required
              fullWidth
            />

            <Input
              label={t('auth.phoneNumber')}
              type="tel"
              placeholder="+358..."
              value={formData.phoneNumber}
              onChange={handleChange('phoneNumber')}
              leftIcon={<Phone className="h-5 w-5 text-gray-400" />}
              autoComplete="tel"
              fullWidth
            />

            <Input
              label={t('auth.password')}
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange('password')}
              leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
              helperText={t('auth.passwordHelper')}
              autoComplete="new-password"
              required
              fullWidth
            />

            <Input
              label={t('auth.confirmPassword')}
              type="password"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              leftIcon={<Lock className="h-5 w-5 text-gray-400" />}
              autoComplete="new-password"
              required
              fullWidth
            />

            <div className="text-xs text-gray-600">
              {t('auth.termsAgreement')}{' '}
              <a href="#" className="text-primary-600 hover:text-primary-700">
                {t('auth.termsOfService')}
              </a>{' '}
              {t('auth.and')}{' '}
              <a href="#" className="text-primary-600 hover:text-primary-700">
                {t('auth.privacyPolicy')}
              </a>.
            </div>

            <Button type="submit" fullWidth loading={loading} size="lg">
              {t('auth.createAccount')}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-4 text-center text-sm text-gray-600">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
              {t('auth.signIn')}
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-4">
          {t('auth.protectedBy')}
        </p>
      </div>
    </div>
  );
};
